<template>
    <LineChart ref="lineChartRef" :id="id" :options="chartOptions" :data="chartData" style="position: relative;" :style="{ maxHeight: height, minHeight: height, width: width }">
    </LineChart>
</template>

<script>
import { Line as LineChart } from 'vue-chartjs'
import { Chart as ChartJS, Title, SubTitle, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale, TimeScale, TimeSeriesScale, Filler } from 'chart.js'
import 'chartjs-adapter-date-fns'

ChartJS.register(Title, SubTitle, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale, TimeScale, TimeSeriesScale, Filler);

export default {
    name: 'LineChart2',
    components: { LineChart },
    props: {
        id: String,
        series: Array,
        period: String,
        theme: String,
        height: {
            type: String,
            default: '260px'
        },
        width: {
            type: String,
            default: '300px'
        },
        xaxis: {
            type: Object,
            default: () => ({})
        },
        yaxis: {
            type: Object,
            default: () => ({
                labels: {
                    show: false
                }
            })
        },
        gridDisplay: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            formats: {
                daily: 'HH:mm',
                monthly: 'yyyy/MM',
                weekly: 'dd/MM/yyyy',
                yearly: 'yyyy/MM'
            },
            chart: null
        }
    },
    computed: {
        chartData() {
            if (!this.series || !this.series[0] || !this.series[0].data) {
                return {
                    labels: [],
                    datasets: []
                }
            }

            return {
                labels: this.series[0].data.map(item => item.x),
                datasets: [{
                    label: this.series[0].name || '',
                    data: this.series[0].data.map(item => item.y),
                    borderColor: this.getCssVar('--line-chart-y-curve-color'),
                    backgroundColor: function(context) {
                        const chart = context.chart;
                        const {ctx, chartArea} = chart;
                        
                        if (!chartArea) {
                            return 'rgba(0, 161, 220, 0.3)';
                        }
                        
                        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                        gradient.addColorStop(0, 'rgba(0, 161, 220, 0.6)');
                        gradient.addColorStop(0.3, 'rgba(0, 161, 220, 0.5)');
                        gradient.addColorStop(0.6, 'rgba(0, 161, 220, 0.3)');
                        gradient.addColorStop(0.8, 'rgba(0, 161, 220, 0.1)');
                        gradient.addColorStop(1, 'rgba(255, 255, 255, 0.0)');
                        return gradient;
                    },
                    borderWidth: 2,
                    fill: 'start',
                    tension: 0.4,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    pointHoverBackgroundColor: this.getCssVar('--line-chart-y-curve-color'),
                    pointHoverBorderColor: '#fff',
                    pointHoverBorderWidth: 2
                }]
            }
        },
        chartOptions() {
            return {
                responsive: !!this.theme,
                maintainAspectRatio: false,
                layout: {
                    padding: {
                        left: 0,
                        right: 10
                    }
                },
                plugins: {
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: this.getCssVar('--line-chart-tooltip-bg-color'),
                        titleColor: this.getCssVar('--line-chart-tooltip-title-color'),
                        bodyColor: this.getCssVar('--line-chart-tooltip-title-color'),
                        borderColor: this.getCssVar('--line-chart-tooltip-border-color'),
                        borderWidth: 1,
                        callbacks: {
                            title: (context) => {
                                const date = new Date(context[0].parsed.x);
                                return this.period === 'daily' ? 
                                    date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) :
                                    date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'numeric', year: 'numeric' });
                            },
                            label: (context) => {
                                return ` ${context.parsed.y.toFixed(2)}`;
                            }
                        }
                    },
                    legend: {
                        display: false
                    }
                },
                scales: {
                    x: {
                        type: 'time',
                        time: {
                            parser: 'yyyy-MM-dd\'T\'HH:mm:ss.SSSxxx',
                            unit: this.getTimeUnit(),
                            displayFormats: {
                                millisecond: this.formats[this.period] || 'HH:mm',
                                second: this.formats[this.period] || 'HH:mm',
                                minute: this.formats[this.period] || 'HH:mm',
                                hour: this.formats[this.period] || 'HH:mm',
                                day: this.formats[this.period] || 'dd/MM',
                                week: this.formats[this.period] || 'dd/MM',
                                month: this.formats[this.period] || 'MM/yyyy',
                                quarter: this.formats[this.period] || 'MM/yyyy',
                                year: this.formats[this.period] || 'yyyy'
                            }
                        },
                        ticks: {
                            color: this.getCssVar('--main-title-color'),
                            font: {
                                family: 'Poppins, Arial, sans-serif',
                                size: this.xaxis?.size || 12,
                                weight: this.xaxis?.weight || 600
                            },
                            maxRotation: 0,
                            minRotation: 0
                        },
                        grid: {
                            display: false
                        },
                        border: {
                            display: true,
                            color: this.getCssVar('--main-title-color'),
                            width: 0.5
                        }
                    },
                    y: {
                        ticks: {
                            display: false,
                            maxTicksLimit: 4
                        },
                        grid: {
                            display: this.gridDisplay,
                            color: this.getCssVar('--line-chart-y-color'),
                            width: 1
                        },
                        border: {
                            display: false,
                        }
                    }
                },
                interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                }
            }
        }
    },
    methods: {
        getTimeUnit() {
            switch (this.period) {
                case 'daily':
                    return 'hour';
                case 'weekly':
                    return 'day';
                case 'monthly':
                    return 'day';
                case 'yearly':
                    return 'month';
                default:
                    return 'day';
            }
        },
        getCssVar(name) {
            const el = document.getElementById('app')
            return getComputedStyle(el).getPropertyValue(name).trim()
        }
    },
    mounted() {
    }
}
</script> 